import { useCallback, useMemo, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useFormContext } from 'react-hook-form'
import classNames from 'classnames'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../../../common/integrations'
import { MappingType } from '../../../../../../../models/Account'
import { useGetIntegrationAdminSettings } from '../../../hooks'
import { IntegrationSettings } from '../../../schema'
import { IntegrationMappingTesterAccordion } from '../../../../TesterTool/TesterAccordion'
import { CalendarSyncMappingTester } from '../../../../TesterTool/Calendar/CalendarSyncMappingTester'
import { SelectCalendarTypeSection } from './SelectCalendarTypeSection'
import { CustomCalendarAdminSettings } from './CustomCalendarAdminSettings'
import styles from './CalendarAdminSettings.module.scss'

export interface CalendarAdminSettingsProps {
  serviceId: IntegrationServiceId
  disabled?: boolean
}

export const CalendarAdminSettings: React.FC<CalendarAdminSettingsProps> = ({ serviceId, disabled }) => {
  const [formKey, setFormKey] = useState<number>(0)
  const { watch } = useFormContext<IntegrationSettings>()
  const currentMappingType = watch('mappings.calendar.mappingType')

  const isCustom = useMemo(() => currentMappingType === MappingType.CUSTOM, [currentMappingType])

  const refreshForm = useCallback(() => {
    setFormKey((prevKey) => prevKey + 1)
  }, [])

  const { isAccountLoading } = useGetIntegrationAdminSettings(serviceId)

  if (isAccountLoading) {
    return (
      <Container>
        <div className={classNames('skeleton w-100 pt-2', styles.skeletonRow)} />
        <div className={classNames('skeleton w-100 pt-2', styles.skeletonRow)} />
      </Container>
    )
  }

  return (
    <Container className='py-3'>
      <Row className='mb-3'>
        <Col>
          <h3 className='mb-2'>Calendar</h3>
          <div className='zocks-text-gray-600'>
            Configure which {getIntegrationDisplayName(serviceId)} object Zocks reads calendar events from, so meetings are picked up the way your firm books
            them.
          </div>
        </Col>
      </Row>
      <Row>
        <SelectCalendarTypeSection onChange={refreshForm} serviceId={serviceId} disabled={disabled} />
      </Row>
      {isCustom ? (
        <>
          <Row>
            <CustomCalendarAdminSettings serviceId={serviceId} formKey={formKey} />
          </Row>
          <Row className='mt-6'>
            <IntegrationMappingTesterAccordion serviceId={serviceId} testerComponents={[CalendarSyncMappingTester]} title='Test Calendar Mapping' />
          </Row>
        </>
      ) : (
        <Row className='mt-7'>
          <Col>
            <p className='zocks-text-gray-600'>Events linked to your contacts through WhoId or WhatId are synced automatically. Nothing else to configure.</p>
          </Col>
        </Row>
      )}
    </Container>
  )
}
